import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { fetchWithAuth } from './auth';
import { getTutorialConfig, TUTORIAL_IDS } from '../config/interactiveTutorialConfig';
import API_BASE_URL from '../config/api';

const InteractiveTutorialContext = createContext();

export const useInteractiveTutorial = () => {
  const context = useContext(InteractiveTutorialContext);
  if (!context) {
    throw new Error('useInteractiveTutorial must be used within InteractiveTutorialProvider');
  }
  return context;
};

export const InteractiveTutorialProvider = ({ children }) => {
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [completedTutorials, setCompletedTutorials] = useState([]);
  const [tutorialContext, setTutorialContext] = useState({}); // Store tutorial-specific data
  const [userActions, setUserActions] = useState({}); // Track user actions per step
  const [unlockedTutorials, setUnlockedTutorials] = useState([
    TUTORIAL_IDS.SEARCH_QUERY, 
    TUTORIAL_IDS.ARTICLE_ANNOTATION, 
    TUTORIAL_IDS.CATEGORY_MANAGEMENT, 
    TUTORIAL_IDS.RESEARCH_PORTAL
  ]); // Start with only Research Portal tutorials unlocked
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [tutorialStatusLoaded, setTutorialStatusLoaded] = useState(false);

  // Start a tutorial
  const startTutorial = useCallback((tutorialId) => {
    const tutorial = getTutorialConfig(tutorialId);
    
    if (!tutorial) {
      console.error(`Tutorial not found: ${tutorialId}`);
      return false;
    }

    setActiveTutorial(tutorial);
    setCurrentStepIndex(0);
    setIsActive(true);
    setTutorialContext({});
    setUserActions({});
    return true;
  }, []);

  // Move to next step
  const nextStep = useCallback(() => {
    if (!activeTutorial) return;

    if (currentStepIndex < activeTutorial.steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
      console.log(`➡️ Moving to step ${currentStepIndex + 2}/${activeTutorial.steps.length}`);
    } else {
      // Tutorial completed
      finishTutorial();
    }
  }, [activeTutorial, currentStepIndex]);

  // Move to previous step
  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      console.log(`⬅️ Moving back to step ${currentStepIndex}/${activeTutorial.steps.length}`);
    }
  }, [currentStepIndex, activeTutorial]);

  // Validate user action for current step
  const validateStepAction = useCallback((stepId, value) => {
    if (!activeTutorial) return false;

    const step = activeTutorial.steps[currentStepIndex];
    if (step.id !== stepId) return false;

    // Store user action
    setUserActions(prev => ({ ...prev, [stepId]: value }));

    // Validate based on step type
    switch (step.type) {
      case 'type_text':
        if (step.validatePartial) {
          // Allow partial matches
          return value.toLowerCase().includes(step.expectedValue.toLowerCase());
        }
        return value.toLowerCase() === step.expectedValue.toLowerCase();

      case 'select_option':
        return value === step.expectedValue;

      case 'select_date':
        // Allow any valid date for now
        return value && value !== '';

      case 'click_button':
        return true; // Click actions auto-advance

      case 'check_checkbox':
        return value === true;

      case 'toggle_switch':
        return value === step.expectedValue;

      default:
        return true;
    }
  }, [activeTutorial, currentStepIndex]);

  // Mark step as completed and advance
  const completeStep = useCallback((stepId) => {
    if (!activeTutorial) return;

    const step = activeTutorial.steps[currentStepIndex];
    if (step.id !== stepId) {
      console.warn(`Step mismatch: expected ${step.id}, got ${stepId}`);
      return;
    }

    console.log(`✅ Step completed: ${stepId}`);
    
    // Auto-advance to next step after a short delay
    setTimeout(() => {
      nextStep();
    }, 500);
  }, [activeTutorial, currentStepIndex, nextStep]);

  // Update tutorial context (for storing dynamic data)
  const updateContext = useCallback((key, value) => {
    setTutorialContext(prev => ({ ...prev, [key]: value }));
  }, []);

  // Mark tutorial as completed in backend
  const markTutorialAsCompleted = async (tutorialId, skipped = false) => {
    try {
      await fetchWithAuth(`/api/user/complete-guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          guide_id: tutorialId,
          skipped: skipped
        })
      });

      // Update local state first
      setCompletedTutorials(prev => [...new Set([...prev, tutorialId])]);
      
      // Reload from backend to ensure sync
      const response = await fetchWithAuth(`${API_BASE_URL}/api/user/guide-status`);
      const data = await response.json();
      if (data.completed_guides) {
        setCompletedTutorials(data.completed_guides);
      }
    } catch (error) {
      console.error('Error marking tutorial as completed:', error);
    }
  };

  // Check if tutorial is completed
  const isTutorialCompleted = useCallback((tutorialId) => {
    return completedTutorials.includes(tutorialId);
  }, [completedTutorials]);

  // Check if tutorial is unlocked
  const isTutorialUnlocked = useCallback((tutorialId) => {
    // If completed, always unlocked (can replay anytime)
    if (completedTutorials.includes(tutorialId)) {
      return true;
    }
    // Otherwise check unlock conditions
    return unlockedTutorials.includes(tutorialId);
  }, [unlockedTutorials, completedTutorials]);

  // Check conditions to unlock tutorials
  const checkUnlockConditions = useCallback(async () => {
    try {
      // Fetch current completion status from backend for real-time accuracy
      const statusResponse = await fetchWithAuth(`${API_BASE_URL}/api/user/guide-status`);
      const statusData = await statusResponse.json();
      const currentCompletedTutorials = statusData.completed_guides || [];
      
      // Also check user data to unlock based on actions
      const queriesResponse = await fetchWithAuth(`${API_BASE_URL}/api/get-searches`);
      const queriesData = await queriesResponse.json();

      // Check if user has at least 1 query with at least 1 article
      const hasQueriesWithArticles = queriesData.metadata && queriesData.metadata.some(query => {
        const articles = query.articles || [];
        return articles.length > 0;
      });

      console.log('🔍 Unlock conditions:', {
        hasQueries: queriesData.metadata?.length > 0,
        hasQueriesWithArticles,
        totalQueries: queriesData.metadata?.length || 0,
        queriesWithArticles: queriesData.metadata?.filter(q => (q.articles || []).length > 0).map(q => ({
          name: q.search_criteria,
          articleCount: (q.articles || []).length
        }))
      });

      // Start with always-unlocked tutorials
      const newUnlocked = [
        TUTORIAL_IDS.RESEARCH_PORTAL,
        TUTORIAL_IDS.SEARCH_QUERY,
        TUTORIAL_IDS.ARTICLE_ANNOTATION,
        TUTORIAL_IDS.CATEGORY_MANAGEMENT
      ];

      // AI Assistant and Collaboration unlock if user has at least 1 query with at least 1 article
      if (hasQueriesWithArticles) {
        newUnlocked.push(TUTORIAL_IDS.AI_SELECT_ARTICLES);
        newUnlocked.push(TUTORIAL_IDS.AI_CONVERSATIONS);
        newUnlocked.push(TUTORIAL_IDS.AI_SHARING);
        newUnlocked.push(TUTORIAL_IDS.AI_ASSISTANT);
        newUnlocked.push(TUTORIAL_IDS.COLLAB_START_CHAT);
        newUnlocked.push(TUTORIAL_IDS.COLLAB_SHARE_CONTENT);
        newUnlocked.push(TUTORIAL_IDS.COLLAB_MANAGE);
        newUnlocked.push(TUTORIAL_IDS.COLLABORATION);
        console.log('✅ Unlocking AI Assistant and Collaboration tutorials');
      } else {
        console.log('🔒 AI Assistant and Collaboration locked - need at least 1 query with articles');
      }

      setUnlockedTutorials(newUnlocked);
    } catch (error) {
      console.error('Error checking unlock conditions:', error);
      // Default to research portal tutorials only
      setUnlockedTutorials([
        TUTORIAL_IDS.SEARCH_QUERY, 
        TUTORIAL_IDS.ARTICLE_ANNOTATION, 
        TUTORIAL_IDS.CATEGORY_MANAGEMENT, 
        TUTORIAL_IDS.RESEARCH_PORTAL
      ]);
    }
  }, []);

  // Load tutorial status from backend
  const loadTutorialStatus = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/user/guide-status`);
      const data = await response.json();
      
      setCompletedTutorials(data.completed_guides || []);
      setIsFirstLogin(data.is_first_login || false);
      setTutorialStatusLoaded(true);
      
      // Check unlock conditions
      await checkUnlockConditions();
      
      return data;
    } catch (error) {
      console.error('Error loading tutorial status:', error);
      setTutorialStatusLoaded(true);
      return null;
    }
  }, [checkUnlockConditions]);

  // Check and unlock next tutorial after completing current one
  const checkAndUnlockNextTutorial = useCallback(async (completedTutorialId) => {
    // checkUnlockConditions now fetches fresh data from backend and handles all unlock logic
    await checkUnlockConditions();
  }, [checkUnlockConditions]);

  // Skip tutorial
  const skipTutorial = useCallback(async () => {
    if (!activeTutorial) return;

    console.log(`⏭️ Skipping tutorial: ${activeTutorial.title}`);
    
    // Mark as completed even if skipped
    await markTutorialAsCompleted(activeTutorial.id, true);
    
    // Check if completing this tutorial unlocks the next one
    await checkAndUnlockNextTutorial(activeTutorial.id);
    
    // Reload tutorial status to update isFirstLogin flag
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/user/guide-status`);
      const data = await response.json();
      setIsFirstLogin(data.is_first_login || false);
    } catch (error) {
      console.error('Error reloading tutorial status:', error);
    }
    
    setIsActive(false);
    setActiveTutorial(null);
    setCurrentStepIndex(0);
    setTutorialContext({});
    setUserActions({});
  }, [activeTutorial, checkAndUnlockNextTutorial]);

  // Finish tutorial
  const finishTutorial = useCallback(async () => {
    if (!activeTutorial) return;

    console.log(`🎉 Tutorial completed: ${activeTutorial.title}`);
    
    // Mark as completed
    await markTutorialAsCompleted(activeTutorial.id, false);
    
    // Check if completing this tutorial unlocks the next one
    await checkAndUnlockNextTutorial(activeTutorial.id);
    
    // Reload tutorial status to update isFirstLogin flag
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/user/guide-status`);
      const data = await response.json();
      setIsFirstLogin(data.is_first_login || false);
    } catch (error) {
      console.error('Error reloading tutorial status:', error);
    }
    
    setIsActive(false);
    setActiveTutorial(null);
    setCurrentStepIndex(0);
    setTutorialContext({});
    setUserActions({});
  }, [activeTutorial, checkAndUnlockNextTutorial]);

  // Load tutorial status on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    
    if (token) {
      // Call inline to avoid dependency issues
      (async () => {
        try {
          const response = await fetchWithAuth(`${API_BASE_URL}/api/user/guide-status`);
          const data = await response.json();
          
          setCompletedTutorials(data.completed_guides || []);
          setIsFirstLogin(data.is_first_login || false);
          setTutorialStatusLoaded(true);
          
          // Check unlock conditions
          await checkUnlockConditions();
        } catch (error) {
          console.error('Error loading tutorial status:', error);
          setTutorialStatusLoaded(true);
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only

  // Get current step
  const getCurrentStep = useCallback(() => {
    if (!activeTutorial || !isActive) return null;
    return activeTutorial.steps[currentStepIndex];
  }, [activeTutorial, currentStepIndex, isActive]);

  // Get progress percentage
  const getProgress = useCallback(() => {
    if (!activeTutorial) return 0;
    return ((currentStepIndex + 1) / activeTutorial.steps.length) * 100;
  }, [activeTutorial, currentStepIndex]);

  const value = {
    // State
    activeTutorial,
    currentStepIndex,
    isActive,
    completedTutorials,
    tutorialContext,
    userActions,
    unlockedTutorials,
    isFirstLogin,
    tutorialStatusLoaded,

    // Actions
    startTutorial,
    nextStep,
    previousStep,
    completeStep,
    skipTutorial,
    finishTutorial,
    validateStepAction,
    updateContext,
    isTutorialCompleted,
    isTutorialUnlocked,
    loadTutorialStatus, // Expose this so SignIn can call it after login
    checkUnlockConditions,

    // Getters
    getCurrentStep,
    getProgress,
    getTotalSteps: () => activeTutorial?.steps.length || 0
  };

  return (
    <InteractiveTutorialContext.Provider value={value}>
      {children}
    </InteractiveTutorialContext.Provider>
  );
};

